import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useHaptic } from '@/app/hooks/useHaptic';

const fieldBase =
  'w-full rounded-control border border-line bg-surface px-3 py-2.5 text-[16px] text-ink placeholder:text-muted ' +
  'bd-focus transition-colors disabled:opacity-60';

function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 mt-3 block text-[12px] font-medium">
      {children}
    </label>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, invalid, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <>
      {label ? <Label htmlFor={fieldId}>{label}</Label> : null}
      <input
        ref={ref}
        id={fieldId}
        className={cn(fieldBase, invalid && 'border-brand-deep', className)}
        aria-invalid={invalid || undefined}
        {...rest}
      />
    </>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <>
      {label ? <Label htmlFor={fieldId}>{label}</Label> : null}
      <textarea
        ref={ref}
        id={fieldId}
        className={cn(fieldBase, 'min-h-[110px] resize-y leading-relaxed', className)}
        {...rest}
      />
    </>
  );
});

export interface OptionCardProps {
  selected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  /** Визуальный тип отметки: точка для выбора одного, галочка для набора. */
  control?: 'radio' | 'checkbox' | 'none';
}

/** Карточка-вариант: используется в анкете, выборе платформы и настройках ИИ. */
export function OptionCard({
  selected,
  disabled,
  onSelect,
  title,
  subtitle,
  icon,
  trailing,
  control = 'radio',
}: OptionCardProps) {
  const haptic = useHaptic();
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      onClick={() => {
        haptic.selection();
        onSelect?.();
      }}
      className={cn(
        'bd-focus mb-2 flex w-full items-center gap-2.5 rounded-control border p-3 text-left transition-colors',
        selected ? 'border-brand bg-surface-2' : 'border-line bg-surface',
        disabled && 'pointer-events-none opacity-55',
      )}
    >
      {icon}
      {control !== 'none' ? (
        <span
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center border transition-colors',
            control === 'radio' ? 'rounded-full' : 'rounded-md',
            selected ? 'border-brand bg-brand text-black' : 'border-line bg-surface',
          )}
          aria-hidden
        >
          {selected ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium leading-snug">{title}</span>
        {subtitle ? (
          <span className="mt-0.5 block text-[11px] leading-[1.35] text-muted">{subtitle}</span>
        ) : null}
      </span>
      {trailing}
    </button>
  );
}
